import type { Project, WindowLayout } from '@cinegrid/core';
import { getMediaBlob, putMediaBlob, setRemoteMediaFetcher } from '../media/mediaStore';
import { useEditorStore } from '../state/editorStore';

const CHANNEL = 'cinegrid-editor-v1';
const HEARTBEAT_MS = 1500;
const LEADER_TIMEOUT_MS = 5000;
const CLAIM_GRACE_MS = 500;
const STATE_RETRY_MS = 1000;

type SyncMessage =
  | { type: 'hello'; tabId: string; label: string; layoutId: string; isLeader: boolean }
  | { type: 'heartbeat'; tabId: string; label: string; layoutId: string; isLeader: boolean }
  | { type: 'leader-claim'; tabId: string; layoutId: string }
  | { type: 'leader-yield'; tabId: string }
  | { type: 'project-patch'; tabId: string; project: Project; playhead: number }
  | { type: 'playhead'; tabId: string; playhead: number; isPlaying: boolean }
  | { type: 'layout-changed'; tabId: string; layout: WindowLayout }
  | { type: 'request-state'; tabId: string }
  | { type: 'full-state'; tabId: string; project: Project; playhead: number; isPlaying: boolean }
  | { type: 'request-media'; tabId: string; assetId: string }
  | {
      type: 'media-blob';
      tabId: string;
      assetId: string;
      mimeType: string;
      name: string;
      buffer: ArrayBuffer;
    }
  | {
      type: 'media-chunk';
      tabId: string;
      assetId: string;
      mimeType: string;
      name: string;
      index: number;
      total: number;
      byteLength: number;
      buffer: ArrayBuffer;
    }
  | { type: 'media-missing'; tabId: string; assetId: string };

/** BroadcastChannel struggles with multi‑MB structured clones — send in pieces. */
const MEDIA_CHUNK_BYTES = 256 * 1024;

interface Peer {
  tabId: string;
  label: string;
  layoutId: string;
  isLeader: boolean;
  lastSeen: number;
}

function isPrimaryLayout(layoutId: string) {
  return layoutId === 'primary';
}

function projectWeight(p: Project) {
  return p.assets.length * 1000 + p.clips.length + (p.settings?.duration ? 1 : 0);
}

/**
 * Cross-tab sync: primary editor is authoritative.
 * Secondary / viewer-only tabs always follow and pull project + media.
 */
export class TabSync {
  private channel: BroadcastChannel | null = null;
  private peers = new Map<string, Peer>();
  private heartbeatTimer: number | null = null;
  private claimTimer: number | null = null;
  private retryTimer: number | null = null;
  private unsubStore: (() => void) | null = null;
  private applyingRemote = false;
  private lastSentProjectAt = 0;
  private knownLeaderId: string | null = null;
  private mediaWaiters = new Map<
    string,
    {
      resolve: (
        v: { buffer: ArrayBuffer; mimeType: string; name: string } | null
      ) => void;
      timer: number;
    }
  >();
  /** Incoming chunked media assembly (assetId → chunks). */
  private mediaChunks = new Map<
    string,
    {
      mimeType: string;
      name: string;
      total: number;
      byteLength: number;
      parts: (ArrayBuffer | null)[];
    }
  >();
  private lastPushedAssetIds = new Set<string>();
  private pushMediaTimer: number | null = null;

  start() {
    if (typeof BroadcastChannel === 'undefined') {
      useEditorStore.getState().setLeader(true);
      return;
    }
    this.channel = new BroadcastChannel(CHANNEL);
    this.channel.onmessage = (ev) => this.onMessage(ev.data as SyncMessage);
    setRemoteMediaFetcher((assetId) => this.requestMedia(assetId));

    useEditorStore.getState().setLeader(false);

    const state = useEditorStore.getState();
    this.post({
      type: 'hello',
      tabId: state.tabId,
      label: state.layout.label,
      layoutId: state.layout.id,
      isLeader: false,
    });
    this.requestState();

    this.claimTimer = window.setTimeout(() => this.tryClaimLeader(), CLAIM_GRACE_MS);
    this.heartbeatTimer = window.setInterval(() => this.heartbeat(), HEARTBEAT_MS);
    // Keep asking until we receive a project (fixes race before leader is ready)
    this.retryTimer = window.setInterval(() => this.maybeRetryStatePull(), STATE_RETRY_MS);

    let prevProject = state.project;
    let prevPlayhead = state.playhead;
    let prevPlaying = state.isPlaying;

    this.unsubStore = useEditorStore.subscribe((s) => {
      if (this.applyingRemote) return;
      if (!s.isLeader) {
        if (s.playhead !== prevPlayhead || s.isPlaying !== prevPlaying) {
          prevPlayhead = s.playhead;
          prevPlaying = s.isPlaying;
          this.post({
            type: 'playhead',
            tabId: s.tabId,
            playhead: s.playhead,
            isPlaying: s.isPlaying,
          });
        }
        prevProject = s.project;
        return;
      }
      if (s.project !== prevProject) {
        prevProject = s.project;
        const now = Date.now();
        if (now - this.lastSentProjectAt > 40) {
          this.lastSentProjectAt = now;
          this.broadcastFullState();
        }
        // Push any newly added media so viewer tabs get overlays / stills
        this.schedulePushAllMedia();
      }
      if (s.playhead !== prevPlayhead || s.isPlaying !== prevPlaying) {
        prevPlayhead = s.playhead;
        prevPlaying = s.isPlaying;
        this.post({
          type: 'playhead',
          tabId: s.tabId,
          playhead: s.playhead,
          isPlaying: s.isPlaying,
        });
      }
    });

    window.addEventListener('beforeunload', this.onUnload);
  }

  stop() {
    const state = useEditorStore.getState();
    if (state.isLeader) {
      this.post({ type: 'leader-yield', tabId: state.tabId });
    }
    window.removeEventListener('beforeunload', this.onUnload);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.claimTimer) clearTimeout(this.claimTimer);
    if (this.retryTimer) clearInterval(this.retryTimer);
    this.unsubStore?.();
    setRemoteMediaFetcher(null);
    for (const [, w] of this.mediaWaiters) {
      clearTimeout(w.timer);
      w.resolve(null);
    }
    this.mediaWaiters.clear();
    this.channel?.close();
  }

  requestMedia(
    assetId: string
  ): Promise<{ buffer: ArrayBuffer; mimeType: string; name: string } | null> {
    if (this.mediaWaiters.has(assetId)) {
      return new Promise((resolve) => {
        const prev = this.mediaWaiters.get(assetId)!;
        const orig = prev.resolve;
        prev.resolve = (v) => {
          orig(v);
          resolve(v);
        };
      });
    }

    return new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        this.mediaWaiters.delete(assetId);
        resolve(null);
      }, 20000);
      this.mediaWaiters.set(assetId, { resolve, timer });
      this.post({
        type: 'request-media',
        tabId: useEditorStore.getState().tabId,
        assetId,
      });
    });
  }

  private onUnload = () => {
    const state = useEditorStore.getState();
    if (state.isLeader) {
      this.post({ type: 'leader-yield', tabId: state.tabId });
    }
  };

  private post(msg: SyncMessage) {
    try {
      this.channel?.postMessage(msg);
    } catch (err) {
      console.warn('tabSync post failed', err);
    }
  }

  private requestState() {
    this.post({ type: 'request-state', tabId: useEditorStore.getState().tabId });
  }

  private broadcastFullState() {
    const s = useEditorStore.getState();
    if (!s.isLeader) return;
    this.post({
      type: 'full-state',
      tabId: s.tabId,
      project: s.project,
      playhead: s.playhead,
      isPlaying: s.isPlaying,
    });
  }

  private maybeRetryStatePull() {
    const s = useEditorStore.getState();
    if (s.isLeader) return;
    if (projectWeight(s.project) > 0) return;
    this.requestState();
  }

  private becomeLeader() {
    const s = useEditorStore.getState();
    if (!s.isLeader) useEditorStore.getState().setLeader(true);
    this.knownLeaderId = s.tabId;
    this.lastPushedAssetIds.clear();
    this.post({
      type: 'leader-claim',
      tabId: s.tabId,
      layoutId: s.layout.id,
    });
    this.broadcastFullState();
    this.schedulePushAllMedia();
  }

  private becomeFollower(leaderId: string | null) {
    const s = useEditorStore.getState();
    if (s.isLeader) useEditorStore.getState().setLeader(false);
    if (leaderId) this.knownLeaderId = leaderId;
    this.requestState();
  }

  private async fulfillMediaRequest(assetId: string, asLeader: boolean) {
    const payload = await this.loadAssetBytes(assetId);
    if (!payload) {
      if (asLeader) {
        this.post({
          type: 'media-missing',
          tabId: useEditorStore.getState().tabId,
          assetId,
        });
      }
      return;
    }
    await this.postMediaBytes(assetId, payload.mimeType, payload.name, payload.buffer);
  }

  private async loadAssetBytes(
    assetId: string
  ): Promise<{ buffer: ArrayBuffer; mimeType: string; name: string } | null> {
    const state = useEditorStore.getState();
    const asset = state.project.assets.find((a) => a.id === assetId);
    if (!asset) return null;
    if (asset.kind === 'adjustment' || asset.kind === 'solid') return null;
    if (!asset.url && asset.sizeBytes === 0) return null;

    let mimeType = asset.mimeType || 'application/octet-stream';
    let name = asset.name || assetId;

    const stored = await getMediaBlob(assetId);
    if (stored?.blob && stored.blob.size > 0) {
      return {
        buffer: await stored.blob.arrayBuffer(),
        mimeType: stored.mimeType || mimeType,
        name: stored.name || name,
      };
    }

    if (asset.url) {
      try {
        const res = await fetch(asset.url);
        const blob = await res.blob();
        if (blob.size === 0) return null;
        mimeType = asset.mimeType || blob.type || mimeType;
        await putMediaBlob(assetId, blob, mimeType, name);
        return { buffer: await blob.arrayBuffer(), mimeType, name };
      } catch {
        return null;
      }
    }
    return null;
  }

  private async postMediaBytes(
    assetId: string,
    mimeType: string,
    name: string,
    buffer: ArrayBuffer
  ) {
    const tabId = useEditorStore.getState().tabId;
    // Small payloads: single message
    if (buffer.byteLength <= MEDIA_CHUNK_BYTES) {
      try {
        this.channel?.postMessage({
          type: 'media-blob',
          tabId,
          assetId,
          mimeType,
          name,
          buffer,
        } satisfies SyncMessage);
        return;
      } catch (err) {
        console.warn('tabSync media-blob failed, chunking', err);
      }
    }

    // Chunked transfer for large stills / footage
    const total = Math.ceil(buffer.byteLength / MEDIA_CHUNK_BYTES) || 1;
    for (let index = 0; index < total; index++) {
      const start = index * MEDIA_CHUNK_BYTES;
      const end = Math.min(buffer.byteLength, start + MEDIA_CHUNK_BYTES);
      const slice = buffer.slice(start, end);
      try {
        this.channel?.postMessage({
          type: 'media-chunk',
          tabId,
          assetId,
          mimeType,
          name,
          index,
          total,
          byteLength: buffer.byteLength,
          buffer: slice,
        } satisfies SyncMessage);
      } catch (err) {
        console.warn('tabSync media-chunk failed', assetId, index, err);
        return;
      }
      // Yield so the channel isn't flooded
      if (index % 4 === 3) {
        await new Promise((r) => window.setTimeout(r, 0));
      }
    }
  }

  private schedulePushAllMedia() {
    if (this.pushMediaTimer != null) return;
    this.pushMediaTimer = window.setTimeout(() => {
      this.pushMediaTimer = null;
      void this.pushAllMedia();
    }, 120);
  }

  /** Leader pushes every file asset so secondary viewers get logos / stills / A/V. */
  private async pushAllMedia() {
    const state = useEditorStore.getState();
    if (!state.isLeader) return;
    for (const asset of state.project.assets) {
      if (asset.kind === 'adjustment' || asset.kind === 'solid') continue;
      if (!asset.url && !(asset.sizeBytes > 0)) continue;
      if (this.lastPushedAssetIds.has(asset.id)) continue;
      const payload = await this.loadAssetBytes(asset.id);
      if (!payload) continue;
      await this.postMediaBytes(asset.id, payload.mimeType, payload.name, payload.buffer);
      this.lastPushedAssetIds.add(asset.id);
    }
  }

  private async ingestRemoteMedia(
    assetId: string,
    mimeType: string,
    name: string,
    buffer: ArrayBuffer
  ) {
    if (!buffer.byteLength) return;
    try {
      const blob = new Blob([buffer], { type: mimeType || 'application/octet-stream' });
      await putMediaBlob(assetId, blob, mimeType, name);
      window.dispatchEvent(
        new CustomEvent('cinegrid-media-ready', { detail: { assetId } })
      );
    } catch (err) {
      console.warn('Failed to store synced media', assetId, err);
    }
    const waiter = this.mediaWaiters.get(assetId);
    if (waiter) {
      clearTimeout(waiter.timer);
      this.mediaWaiters.delete(assetId);
      waiter.resolve({ buffer, mimeType, name });
    }
  }

  private onMediaChunk(msg: Extract<SyncMessage, { type: 'media-chunk' }>) {
    let entry = this.mediaChunks.get(msg.assetId);
    if (!entry || entry.total !== msg.total || entry.byteLength !== msg.byteLength) {
      entry = {
        mimeType: msg.mimeType,
        name: msg.name,
        total: msg.total,
        byteLength: msg.byteLength,
        parts: Array.from({ length: msg.total }, () => null),
      };
      this.mediaChunks.set(msg.assetId, entry);
    }
    entry.parts[msg.index] = msg.buffer;
    if (entry.parts.some((p) => p == null)) return;

    const out = new Uint8Array(entry.byteLength);
    let offset = 0;
    for (const part of entry.parts) {
      const u8 = new Uint8Array(part!);
      out.set(u8, offset);
      offset += u8.byteLength;
    }
    this.mediaChunks.delete(msg.assetId);
    void this.ingestRemoteMedia(msg.assetId, entry.mimeType, entry.name, out.buffer);
  }

  private applyRemoteProject(
    fromTabId: string,
    project: Project,
    playhead: number,
    isPlaying?: boolean
  ) {
    const state = useEditorStore.getState();
    const remoteW = projectWeight(project);
    const localW = projectWeight(state.project);

    // Never replace a filled project with an empty one
    if (remoteW === 0 && localW > 0) return;

    // False leader with emptier project: yield and accept
    if (state.isLeader) {
      if (remoteW > localW) {
        useEditorStore.getState().setLeader(false);
        this.knownLeaderId = fromTabId;
      } else {
        return;
      }
    }

    this.knownLeaderId = fromTabId;
    this.applyingRemote = true;
    useEditorStore.getState().setProject(project);
    useEditorStore.getState().setPlayhead(playhead);
    if (isPlaying !== undefined) {
      useEditorStore.getState().setPlaying(isPlaying);
    }
    this.applyingRemote = false;

    // Pull any media the follower does not have yet (logos / stills / clips)
    for (const asset of project.assets) {
      if (asset.kind === 'adjustment' || asset.kind === 'solid') continue;
      if (!asset.url && !(asset.sizeBytes > 0)) continue;
      void getMediaBlob(asset.id).then((stored) => {
        if (stored?.blob && stored.blob.size > 0) return;
        void this.requestMedia(asset.id);
      });
    }
  }

  private notePeer(msg: {
    tabId: string;
    label?: string;
    layoutId: string;
    isLeader: boolean;
  }) {
    const prev = this.peers.get(msg.tabId);
    this.peers.set(msg.tabId, {
      tabId: msg.tabId,
      label: msg.label ?? prev?.label ?? '',
      layoutId: msg.layoutId,
      isLeader: msg.isLeader,
      lastSeen: Date.now(),
    });
    if (msg.isLeader) {
      const was = this.knownLeaderId;
      this.knownLeaderId = msg.tabId;
      // New leader appeared — pull state if we have nothing
      if (was !== msg.tabId && !useEditorStore.getState().isLeader) {
        this.requestState();
      }
    }
    this.publishPeers();
  }

  private onMessage(msg: SyncMessage) {
    const state = useEditorStore.getState();
    if ('tabId' in msg && msg.tabId === state.tabId) return;

    switch (msg.type) {
      case 'hello':
        this.notePeer(msg);
        if (state.isLeader) {
          // New viewer tab — re-push media (lastPushed would otherwise skip)
          this.lastPushedAssetIds.clear();
          this.broadcastFullState();
          this.schedulePushAllMedia();
        }
        // Primary peer exists — secondary must not stay/become leader
        if (
          isPrimaryLayout(msg.layoutId) &&
          !isPrimaryLayout(state.layout.id) &&
          state.isLeader
        ) {
          this.becomeFollower(msg.isLeader ? msg.tabId : null);
        }
        break;
      case 'heartbeat':
        this.notePeer(msg);
        if (
          isPrimaryLayout(msg.layoutId) &&
          !isPrimaryLayout(state.layout.id) &&
          state.isLeader
        ) {
          this.becomeFollower(msg.isLeader ? msg.tabId : null);
        }
        break;
      case 'leader-claim': {
        this.notePeer({
          tabId: msg.tabId,
          layoutId: msg.layoutId,
          isLeader: true,
        });
        if (state.isLeader) {
          const theyPrimary = isPrimaryLayout(msg.layoutId);
          const wePrimary = isPrimaryLayout(state.layout.id);
          const lose =
            (theyPrimary && !wePrimary) ||
            (theyPrimary === wePrimary && state.tabId > msg.tabId);
          if (lose) this.becomeFollower(msg.tabId);
        } else {
          this.knownLeaderId = msg.tabId;
          this.requestState();
        }
        break;
      }
      case 'leader-yield':
        this.peers.delete(msg.tabId);
        if (this.knownLeaderId === msg.tabId) this.knownLeaderId = null;
        this.tryClaimLeader();
        this.publishPeers();
        break;
      case 'request-state':
        if (state.isLeader) {
          this.broadcastFullState();
          this.schedulePushAllMedia();
        }
        break;
      case 'full-state':
        this.applyRemoteProject(msg.tabId, msg.project, msg.playhead, msg.isPlaying);
        break;
      case 'project-patch':
        this.applyRemoteProject(msg.tabId, msg.project, msg.playhead);
        break;
      case 'playhead':
        if (!state.isLeader) {
          this.applyingRemote = true;
          useEditorStore.getState().setPlayhead(msg.playhead);
          useEditorStore.getState().setPlaying(msg.isPlaying);
          this.applyingRemote = false;
        }
        break;
      case 'layout-changed':
        this.notePeer({
          tabId: msg.tabId,
          label: msg.layout.label,
          layoutId: msg.layout.id,
          isLeader: this.peers.get(msg.tabId)?.isLeader ?? false,
        });
        this.tryClaimLeader();
        break;
      case 'request-media':
        void this.fulfillMediaRequest(msg.assetId, state.isLeader);
        break;
      case 'media-blob':
        void this.ingestRemoteMedia(msg.assetId, msg.mimeType, msg.name, msg.buffer);
        break;
      case 'media-chunk':
        this.onMediaChunk(msg);
        break;
      case 'media-missing': {
        const waiter = this.mediaWaiters.get(msg.assetId);
        if (waiter) {
          clearTimeout(waiter.timer);
          this.mediaWaiters.delete(msg.assetId);
          waiter.resolve(null);
        }
        break;
      }
    }
  }

  private heartbeat() {
    const state = useEditorStore.getState();
    const now = Date.now();
    for (const [id, peer] of this.peers) {
      if (now - peer.lastSeen > LEADER_TIMEOUT_MS) {
        this.peers.delete(id);
        if (peer.isLeader || this.knownLeaderId === id) {
          this.knownLeaderId = null;
          this.tryClaimLeader();
        }
      }
    }
    this.post({
      type: 'heartbeat',
      tabId: state.tabId,
      label: state.layout.label,
      layoutId: state.layout.id,
      isLeader: state.isLeader,
    });
    this.publishPeers();
  }

  private tryClaimLeader() {
    const state = useEditorStore.getState();

    // Viewer / color tabs never lead while a primary editor tab is alive
    const primaryPeer = [...this.peers.values()].find((p) =>
      isPrimaryLayout(p.layoutId)
    );
    if (!isPrimaryLayout(state.layout.id) && primaryPeer) {
      if (state.isLeader) this.becomeFollower(primaryPeer.isLeader ? primaryPeer.tabId : null);
      else if (projectWeight(state.project) === 0) this.requestState();
      return;
    }

    const peerLeader = [...this.peers.values()].find((p) => p.isLeader);
    if (peerLeader && peerLeader.tabId !== state.tabId) {
      const theyPrimary = isPrimaryLayout(peerLeader.layoutId);
      const wePrimary = isPrimaryLayout(state.layout.id);
      if (wePrimary && !theyPrimary) {
        this.becomeLeader();
        return;
      }
      this.becomeFollower(peerLeader.tabId);
      return;
    }

    this.becomeLeader();
  }

  private publishPeers() {
    const state = useEditorStore.getState();
    useEditorStore.getState().setConnectedTabs([
      {
        tabId: state.tabId,
        label: state.layout.label,
        layoutId: state.layout.id,
        isLeader: state.isLeader,
      },
      ...[...this.peers.values()].map((p) => ({
        tabId: p.tabId,
        label: p.label,
        layoutId: p.layoutId,
        isLeader: p.isLeader,
      })),
    ]);
  }

  announceLayout(layout: WindowLayout) {
    const state = useEditorStore.getState();
    this.post({ type: 'layout-changed', tabId: state.tabId, layout });
    this.tryClaimLeader();
  }
}

export const tabSync = new TabSync();
